import React, { useEffect, useMemo, useState } from 'react';
import ContentBody from './content-body';
import ContentHeader from './content-header';
import { EventsState } from '../../../store/types';
import { useDispatch, useSelector, batch } from 'react-redux';
import { clearChecks, getDocuments, getUstreamVideos, getWistiaVideos } from '../../../store/actions/admin/content-actions';
import { loadColorPacks, loadFontPacks } from '../../../store/actions/admin/create-event';
import { FILE_TYPES, sortDocs, sortMiscModule, sortStaticVideos } from '../../../store/utils/content-utils';
import { basicSearch } from '../../../utils/utils';
import VideoItem from './video-item';
import { FontPack, Color, UstreamVideo, StaticVideo, WistiaVideo, HomepageVideo, IVSRecording } from '../../../types/working-model';
import FontItem from './font-item';
import PaletteItem from './palette-item';
import DocumentItem from './document-item';
import { gridPageCount, listPageCount } from '../products/products-main';
import './content-item.scss';
import { getChannelRecordings, getChannelUpVideos } from '../../../store/actions/admin/videos';
import useTranslationModules from '../../../i18n/useTranslationModules';

const ContentMain: React.FC = () => {
	const {
		AuthReducer: {
			user,
			token
		},
		ContentReducer: {
			fileType,
			sortBy,
			searchContent: search,
			filteredDocs: documents,
			ustreamVideos,
			view,
			wistiaVideos
		},
		CreateEventReducer: {
			colorPacks: palettes,
			fontPacks: fonts
		},
		VideosReducer: {
			videos,
			recordedVideos
		}
	} = useSelector((state: EventsState) => state);

	const [page, setPage] = useState(1);
	const pageSize = (view === "grid") ? gridPageCount : listPageCount;

	const ibmVideos = ustreamVideos.videos;

	const dispatch = useDispatch();
	useTranslationModules({ language: "en" });

	useEffect(() => {
		batch(() => {
			dispatch(getDocuments(user!.active_channel, token!));
			dispatch(getUstreamVideos(user!.active_channel, token!));
			dispatch(loadFontPacks(user!.active_channel, token!));
			dispatch(loadColorPacks(token!, user!.active_channel));
			dispatch(getWistiaVideos(user!.active_channel, token!));
			dispatch(getChannelUpVideos(token!, user!.active_channel));
			dispatch(getChannelRecordings(token!, user!.active_channel));
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token]);

	//need to reconcile all incoming videos to compatible types
	const [uploadedVideos, combinedRecordedVideos] = useMemo(() => {
		const retypedIbm = ibmVideos?.map((video: UstreamVideo): StaticVideo => ({
			video: Number(video.id),
			type: "ustream",
			name: video.title,
			image: video.thumbnail.default,
			duration: Number(video.length),
			created_date: new Date(video.created_at * 1000)
		})) ?? [];

		const retypedWistia = wistiaVideos?.medias.map((video: WistiaVideo): StaticVideo => ({
			video: video.hashed_id,
			type: "wistia",
			name: video.name,
			image: video.thumbnail.url,
			duration: video.duration,
			created_date: new Date(video.created)
		})) ?? [];

		const retypedUploaded = videos.map((video: HomepageVideo): StaticVideo => ({
			...video,
			created_date: new Date(video.created_at),
			duration: video.duration ?? 0,
			source: video.source
		}));

		const retypedRecordings = recordedVideos.map((video: IVSRecording): StaticVideo => ({
			video: video.id,
			type: "recorded",
			name: video.name,
			image: '',
			duration: video.recording_duration_ms,
			created_date: new Date(video.timestamp),
			hls_link: video.playback_url,
			uuid: video.uuid
		}));

		return [[...retypedUploaded, ...retypedWistia], [...retypedRecordings, ...retypedIbm]];
	}, [ibmVideos, wistiaVideos, videos, recordedVideos]);

	useEffect(() => {
		dispatch(clearChecks());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	//Searching, Sorting, Paging
	const { displayList, idList, totalPages } = useMemo(() => {
		const displayList: JSX.Element[] = [];
		const idList: (number | string)[] = []; //used for CHECK_All action
		let totalPages = 0;
		//paging Indexs
		const indexStart = (page - 1) * pageSize;
		const indexEnd = indexStart + pageSize;
		switch (fileType) {
			case (FILE_TYPES.UPLOADED): {
				const videosSearch = search ? uploadedVideos.filter(vid => basicSearch(vid.name, search)) : uploadedVideos;
				const videosSort = sortBy ? sortStaticVideos(videosSearch ?? [], sortBy) : (videosSearch ?? []);

				totalPages = Math.ceil(videosSort.length / pageSize);
				const videosPage = videosSort.slice(indexStart, indexEnd);

				videosPage.forEach(vid => {
					displayList.push(<VideoItem key={vid.video} video={vid} />);
					idList.push(vid.video);
				});
				break;
			}

			case (FILE_TYPES.RECORDED): {
				const videosSearch = search ? combinedRecordedVideos.filter(vid => basicSearch(vid.name, search)) : combinedRecordedVideos;
				const videosSort = sortBy ? sortStaticVideos(videosSearch, sortBy) : videosSearch;

				totalPages = Math.ceil(videosSort.length / pageSize);
				const videosPage = videosSort.slice(indexStart, indexEnd);

				videosPage.forEach(vid => {
					displayList.push(<VideoItem key={vid.video} video={vid} />);
					idList.push(vid.video);
				});
				break;
			}
			case (FILE_TYPES.FONTS): {
				const fontsSearch = search ? fonts.filter(font => basicSearch(font.title, search)) : fonts;
				const fontsSort = sortBy ? sortMiscModule(fontsSearch, sortBy) as FontPack[] : fontsSearch;

				totalPages = Math.ceil(fontsSort.length / pageSize);
				const fontsPage = fontsSort.slice(indexStart, indexEnd);

				fontsPage.forEach(font => {
					displayList.push(<FontItem key={font.font_pack} font={font} />);
					idList.push(font.font_pack);
				});
				break;
			}
			case (FILE_TYPES.PALETTES): {
				const palettesSearch = search ? palettes.filter(pal => basicSearch(pal.title, search)) : palettes;
				const palettesSort = sortBy ? sortMiscModule(palettesSearch, sortBy) as Color[] : palettesSearch;

				totalPages = Math.ceil(palettesSort.length / pageSize);
				const palettesPage = palettesSort.slice(indexStart, indexEnd);

				palettesPage.forEach(pal => {
					displayList.push(<PaletteItem key={pal.color_pack} palette={pal} />);
					idList.push(pal.color_pack);
				});
				break;
			}
			default: {
				//Documents and Images
				const docsSearch = search ? documents.filter(doc => basicSearch(doc.display_name.base, search)) : documents;
				const docsSort = sortBy ? sortDocs(docsSearch, sortBy) : docsSearch;

				totalPages = Math.ceil(docsSort.length / pageSize);
				const docsPage = docsSort.slice(indexStart, indexEnd);

				docsPage.forEach(doc => {
					displayList.push(<DocumentItem key={doc.document} document={doc} />);
					idList.push(doc.document);
				});
			}
		}

		return { displayList, idList, totalPages };

	}, [fileType, sortBy, search, documents, fonts, palettes, page, pageSize, combinedRecordedVideos, uploadedVideos]);

	return (
		<main className="wrapper content-main">
			<div className="container">
				<ContentHeader idList={idList} />
				<hr className="hr" />
				<ContentBody
					displayList={displayList}
					idList={idList}
					pagingState={{ page, setPage, totalPages }}
				/>
			</div>
		</main>
	);
};

export default ContentMain;
